/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { useCatalogEntryList } from '@/data/Content/CatalogEntryList';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { Chip, Stack, IconButton, Box, ButtonBase } from '@mui/material';
import { catalogEntryListFacetChipsSX } from '@/components/content/CatalogEntryList/styles/facetChips';
import { FC, Fragment, useContext } from 'react';
import { ID } from '@/data/types/Basic';
import CloseIcon from '@mui/icons-material/Close';
import {
	CatalogEntryListFacetChipsCloseIconFrameSX,
	CatalogEntryListFacetChipsCloseIconSX,
	CatalogEntryListFacetChipsSecondarySX,
} from '@/components/content/CatalogEntryList/styles/main';
import { PLPOfferRecommendation } from '@/components/blocks/PLPOfferRecommendation';

// eslint-disable-next-line complexity
export const CatalogEntryListFacetChips: FC<{ id: ID; isFilter?: boolean | string }> = ({
	id,
	isFilter,
}) => {
	const {
		onDeleteAll,
		onFacetDelete,
		onPriceSelectionDelete,
		selectedFacet,
		facetEntries,
		filteredParams,
	} = useContext(ContentContext) as ReturnType<typeof useCatalogEntryList>;
	const productGridNLS = useLocalization('ProductGrid');
	const { minPrice = -1, maxPrice = -1 } = filteredParams;

	if (isFilter) {
		return (
			<Fragment>
				<Stack
					direction="row"
					alignItems="baseline"
					justifyContent="flex-start"
					flexWrap="wrap"
					gap={1}
					sx={catalogEntryListFacetChipsSX}
				>
					{selectedFacet.map((facetValue) => (
						<Chip
							sx={CatalogEntryListFacetChipsSecondarySX}
							deleteIcon={
								<IconButton sx={CatalogEntryListFacetChipsCloseIconFrameSX} size="small">
									<CloseIcon sx={CatalogEntryListFacetChipsCloseIconSX} />
								</IconButton>
							}
							key={facetValue}
							size="medium"
							variant="outlined"
							label={facetEntries.find((entry) => entry.value === facetValue)?.label}
							onClick={onFacetDelete(facetValue)}
							onDelete={onFacetDelete(facetValue)}
							data-testid={`catalog-entry-list-${facetValue}-selected-facet-chip`}
							id={`catalog-entry-list-${facetValue}-selected-facet-chip`}
						/>
					))}
					{minPrice > -1 && maxPrice > -1 ? (
						<Chip
							sx={CatalogEntryListFacetChipsSecondarySX}
							deleteIcon={
								<IconButton sx={CatalogEntryListFacetChipsCloseIconFrameSX} size="small">
									<CloseIcon sx={CatalogEntryListFacetChipsCloseIconSX} />
								</IconButton>
							}
							label={<PriceDisplay min={minPrice === 0 ? 1 : minPrice} max={maxPrice} />}
							onClick={onPriceSelectionDelete}
							onDelete={onPriceSelectionDelete}
							data-testid="catalog-entry-list-formatted-price-chip"
							id="catalog-entry-list-formatted-price-chip"
						/>
					) : null}
				</Stack>
				{selectedFacet.length > 0 ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: { xs: 'flex-end', md: 'flex-start' },
							marginTop: { xs: 0, sm: 1 },
							marginBottom: { xs: 0, md: 1, lg: 0 },
						}}
					>
						<ButtonBase
							id="clear-all-facets"
							data-testid="clear-all-facets"
							onClick={onDeleteAll}
							sx={{
								fontSize: { xs: 'var(--font-size-sm)', sm: 'var(--font-size-small)' },
								color: 'primary.main',
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
						>
							{productGridNLS.Actions.clearAll.t()}
						</ButtonBase>
					</Box>
				) : null}
			</Fragment>
		);
	}

	return <PLPOfferRecommendation id={id} />;
};
